/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import React from "react";
import Paper from "@mui/material/Paper";
import FormFader from "../Common/Fader/FormFader";
import Hidden from "@mui/material/Hidden";
import Grid from "@mui/material/Grid";
import FormSubmit from "../Common/FormSubmit/FormSubmit";
import { useForm } from "../../../provider/Form/FormProvider";
import ValidatorTextField from "../../FormFields/ValidatorTextField";
import Typography from "@mui/material/Typography";
import FormLegend from "../Common/FormLegend/FormLegend";
import FormSubmitProps from "../../../types/FormSubmitProps";
import wizardStyle from "../wizardStyle";
import { Alert, Box } from "@mui/material";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import { fetchResource, queryKeys } from "../../../services/reactQuery/reactQueryService";
import { useQuery } from "@tanstack/react-query";
import { getProductData } from "../../../services/productService";
import WaitingForAiResponse from "../Common/WaitingForAiResponse/WaitingForAiResponse";

type RefinementProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
};

export default function Refinement({ submitProps, product }: RefinementProps) {
  const { handleSubmit } = useForm();

  useQuery({
    queryKey: queryKeys.item(getProductData(product.name, "apiUri"), product.id),
    queryFn: () => fetchResource(getProductData(product.name, "apiUri"), product.id),
    enabled: product.aiRefinementQuestions === null || product.aiInitialAssessment === null,
    refetchInterval: (data) => {
      const stillWaiting = data.aiRefinementQuestions === null || data.aiInitialAssessment === null;

      return stillWaiting ? 2000 : false;
    },
    refetchIntervalInBackground: true,
  });

  const isWaitingForAiResponse = product.aiRefinementQuestions === null || product.aiInitialAssessment === null;

  if (isWaitingForAiResponse) {
    return (
      <WaitingForAiResponse
        mobileHeadline={"Ihr Anliegen"}
        headline={"Wir haben Ihr Anliegen wie folgt verstanden:"}
        text={"Ihr Anliegen wird gerade analysiert und zusammengefasst.<br />Bitte laden Sie die Seite nicht neu."}
      />
    );
  }

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Hidden smUp>
            <Typography variant={"h4"} sx={wizardStyle.headline}>
              Ihr Anliegen
            </Typography>
          </Hidden>
          <Grid container justifyContent={"flex-start"} alignItems={"baseline"}>
            <Grid item xs={12}>
              <FormLegend>
                <strong>Wir haben Ihr Anliegen wie folgt verstanden:</strong>
              </FormLegend>
            </Grid>
            {!isWaitingForAiResponse && (
              <>
                <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
                  <FormLegend>{product.aiInitialAssessment}</FormLegend>
                </Grid>
                <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
                  <Alert
                    severity="info"
                    sx={{
                      mt: "1rem",
                      mb: "2rem",
                      color: (theme) => theme.palette.info.dark,
                    }}
                  >
                    <Typography variant="h6">Bitte lassen Sie uns noch folgende Informationen zukommen:</Typography>
                    <Typography dangerouslySetInnerHTML={{ __html: product.aiRefinementQuestions }} />
                  </Alert>
                </Grid>
                <Grid item xs={12}>
                  <ValidatorTextField
                    name="refinementAnswers"
                    label="Ergänzende Informationen"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    multiline
                    rows={10}
                  />
                </Grid>
              </>
            )}
          </Grid>
          <FormSubmit {...submitProps} disabled={isWaitingForAiResponse} />
        </Box>
      </FormFader>
    </Paper>
  );
}
